import { getCurrentOrg } from "#webapp/src/util/org.util";
import { useQuery } from "@apollo/client";
import { getValue } from "@clockwise/client-commons/src/util/errorable.util";
import { Button } from "@clockwise/design-system";
import { Tabs } from "@clockwise/design-system/src/components/Tabs";
import { AutoFixHighFilled, ChevronLeft, ChevronRight } from "@clockwise/icons";
import { Nav } from "@clockwise/web-commons/src/components/nav";
import { useReadActiveEvent } from "@clockwise/web-commons/src/util/ActiveEventContext";
import { useUpdateCalendar } from "@clockwise/web-commons/src/util/CalendarContext";
import { useExperiment } from "@clockwise/web-commons/src/util/ExperimentsContext";
import { useIsOutlook } from "@clockwise/web-commons/src/util/OutlookContext";
import { PageEvents, useTrackPageview } from "@clockwise/web-commons/src/util/analytics.util";
import { isElectron } from "@clockwise/web-commons/src/util/browser.util";
import {
  useElementDimensions,
  useIsInIframe,
  useWindowFocus,
} from "@clockwise/web-commons/src/util/react.util";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { Close } from "@material-ui/icons";
import classNames from "classnames";
import { useRouter } from "found";
import { compact } from "lodash";
import { DateTime } from "luxon";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocalStorage } from "usehooks-ts";
import { IViewer } from "../../__schema__";
import { paths } from "../../constants/site.constant";
import { useFeatureFlag } from "../../launch-darkly";
import { setMenuDrawerOpen } from "../../state/actions/chrome-extension.actions";
import { IReduxState } from "../../state/reducers/root.reducer";
import { hashHistory } from "../../util/history.util";
import AIChatPlusCalendar from "../chat-plus-calendar/AIChatPlusCalendar";
import { ChromeMenuDrawer } from "../chrome-menu-drawer";
import { RouteRenderer } from "../route-renderer";
import { EnsureLinkName } from "../scheduling-link/EnsureLinkName";
import {
  MAX_HEIGHT,
  SchedulingLinksCompactGateway,
  setPopupHeight,
} from "../scheduling-link/SchedulingLinksCompactGateway";
import { PlannerProvider } from "../web-app-calendar/Context";
import { PlannerMiniDay } from "../web-app-calendar/PlannerMiniDay";
import { PlannerMiniEvent } from "../web-app-calendar/PlannerMiniEvent";
import { usePlannerEventCards } from "../web-app-calendar/hooks/usePlannerEventCards";
import { getCalendarHashFromState } from "../web-app-calendar/planner-state-persistance/util/getCalendarHashFromState";
import { parseCalendarIdsGQL } from "../web-app-calendar/util/parseCalendarIdsGQL";
import { parseColorSettingsGQL } from "../web-app-calendar/util/parseColorSettingsGQL";
import { MenuBarUpdater } from "./MenuBarUpdater";
import { Settings } from "./Settings";
import { ClockwiseMiniDocument } from "./__generated__/ClockwiseMini.generated";

const CONTENT_HEIGHT_BUFFER = 90;

type MiniTab = "planner_mini" | "links_compact";
// Keep list of ordered tabs to ensure that the tab order is consistent and to be able to map index to tab name to store in local storage.
const orderedTabs: MiniTab[] = ["planner_mini", "links_compact"];

export const ClockwiseMini = () => {
  const { data, loading, error } = useQuery(ClockwiseMiniDocument, {
    fetchPolicy: "cache-and-network",
  });

  const { router } = useRouter();
  const onPlannerMini = useFeatureFlag("PlannerMini");
  const isOnDebugMini = useExperiment("DebugMini");
  const dispatch = useDispatch();
  const menuDrawerOpen = useSelector((state: IReduxState) => state.webExtension.menuDrawerOpen);
  const authed = useSelector((state: IReduxState) => state.auth.authed);
  const isOutlook = useIsOutlook();
  const isInIframe = useIsInIframe();
  const [contentEl, setContentEl] = useState<HTMLDivElement | null>(null);
  const [clockwiseMiniTab, setClockwiseMiniTab] = useLocalStorage<MiniTab>(
    "ClockwiseMini:ActiveTab",
    "planner_mini",
  );
  const contentHeight = useElementDimensions(contentEl).height;

  // TODO prevent double click from taking the tray fullscreen. Found that Loom and Krisp also suffer from this.
  // useEffect(() => {
  //   window.ondblclick = () => {
  //     // Prevent
  //     return false;
  //   };
  // }, []);

  // Track
  useTrackPageview(PageEvents.CLOCKWISE_MINI, {
    isOutlook,
    extensionDropdown: isInIframe && !isOutlook && !isElectron,
    desktopApp: isElectron,
  });

  // Close the popup if the user is not authenticated and not in Outlook. Happens when they logout from popup.
  if (!isOutlook && !authed) {
    if (isInIframe) {
      window.parent.close();
    } else {
      window.close();
    }
  }

  // Close the window if not in Outlook and the view is not in an iframe. Happens when they get redirected to the login page
  // from the popup and then they login.
  if (!isOnDebugMini && !isOutlook && authed && !isInIframe && !isElectron) {
    router.replace(paths.webApp);
  }

  useEffect(() => {
    if (menuDrawerOpen) {
      setPopupHeight(MAX_HEIGHT);
    }
  }, [menuDrawerOpen]);

  const viewer = getValue(data?.viewer, "Viewer");

  const [selectedDate, setSelectedDate] = useState(DateTime.now().setZone(getRenderTimeZone()));
  const lastFocusedDate = useRef<DateTime>(selectedDate);
  const calendarDispatch = useUpdateCalendar();

  const isWindowFocused = useWindowFocus();
  // Upon focus, if selectedDay is before today, set to today. This ensures today is loaded if you haven't opened
  // the popup yet today and its still on yesterday.
  useEffect(() => {
    if (!isWindowFocused) {
      return;
    }

    const now = DateTime.now().setZone(getRenderTimeZone());
    if (lastFocusedDate.current < now.startOf("day")) {
      setSelectedDate(now);
    }

    lastFocusedDate.current = now;
  }, [isWindowFocused]); // eslint-disable-line react-hooks/exhaustive-deps

  // On date Change: Update Route Hash. Triggers PlannerProvider to fetch new data.
  useEffect(() => {
    const calendarState = {
      visibleDates: [selectedDate.toISODate()],
      focusedDate: selectedDate.toISODate(),
    };

    const calendarHash = getCalendarHashFromState(calendarState);
    hashHistory.replace(calendarHash);

    // Dispatch action to CalendarProvider to fetch new data.
    calendarDispatch({
      type: "set-view",
      payload: { view: "day", date: selectedDate.toISODate() },
    });
  }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const tabs = useMemo(() => {
    const isToday = selectedDate.toISODate() === DateTime.now().toISODate();
    return compact([
      onPlannerMini && {
        title: (
          <div
            className="cw-uppercase cw-flex cw-items-center cw-justify-between cw-gap-1"
            key="planner-mini"
          >
            <a
              className="-cw-mb-[3px]"
              onClick={() => setSelectedDate(selectedDate.minus({ days: 1 }))}
            >
              <ChevronLeft />
            </a>
            <div className="cw-flex cw-items-center cw-gap-2 cw-text-12">
              {isToday ? "Today" : selectedDate.toFormat("EEE")}
              <span className="cw-font-medium">{selectedDate.toFormat("MMMM d")}</span>
            </div>
            <a
              className="-cw-mb-[3px]"
              onClick={() => setSelectedDate(selectedDate.plus({ days: 1 }))}
            >
              <ChevronRight />
            </a>
          </div>
        ),
        content: (
          <div className="-cw-mt-5">
            <PlannerMiniDay
              selectedDate={selectedDate}
              userId={viewer?.user?.id || ""}
              onChangeDate={setSelectedDate}
              openChalendar={() => setChalendarOpen(true)}
            />
          </div>
        ),
        key: "planner_mini",
      },
      {
        title: <div className="cw-uppercase cw-text-12">Links</div>,
        content: (
          <div className="cw-mx-4">
            <EnsureLinkName>
              <SchedulingLinksCompactGateway surface="mini" />
            </EnsureLinkName>
          </div>
        ),
        key: "links_compact",
      },
    ]);
  }, [onPlannerMini, selectedDate, viewer?.user?.id]);

  const setActiveTabFromTabsIndex = useCallback(
    (index: number) => {
      const activeTab = orderedTabs[index];
      setClockwiseMiniTab(activeTab);
    },
    [setClockwiseMiniTab],
  );

  const [chalendarOpen, setChalendarOpen] = useState(false);
  const activeEvent = useReadActiveEvent();

  // On tab change, setPopupHeight to the height of the content
  useEffect(() => {
    if (chalendarOpen || !!activeEvent) {
      setPopupHeight(MAX_HEIGHT);
      return;
    }
    if (contentEl?.scrollHeight && !menuDrawerOpen) {
      setPopupHeight(contentEl.scrollHeight + CONTENT_HEIGHT_BUFFER);
    }
  }, [clockwiseMiniTab, contentHeight, menuDrawerOpen, chalendarOpen, activeEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  const org = getCurrentOrg(viewer);
  const calendarIds = parseCalendarIdsGQL(org);
  const colorSettings = parseColorSettingsGQL(org);

  const inElectronCanShowSettings = isElectron && window.tray?.nextEventTitle;
  const [showClockwiseAI, loadingFlag] = useFeatureFlag("ClockwiseAI");
  const [showClockwiseAIMini] = useFeatureFlag("ClockwiseAIMini");

  return (
    <PlannerProvider
      useEventCardsHook={usePlannerEventCards}
      userCalendarIds={calendarIds}
      colorSettings={colorSettings}
      workingHourBounds={null}
      workingHours={null}
    >
      <RouteRenderer
        error={!!error && !loading && !loadingFlag}
        loading={loading && !data && loadingFlag}
      >
        {/* Load MenuBarUpdater in its own Component, separate of the tabs/views of ClockwiseMini, so that it keeps updating the menu bar in the background */}
        <MenuBarUpdater userCalendarIds={calendarIds} />
        <ChromeMenuDrawer viewer={viewer as IViewer} open={menuDrawerOpen} style={{ zIndex: 30 }} />
        <div className="cw-flex cw-items-center cw-mr-2">
          <div className="cw-grow">
            <Nav
              showMenu={true}
              showCollapse={false}
              onClickCollapse={() => {}}
              onClickMenu={() => dispatch(setMenuDrawerOpen(true))}
            />
          </div>
          {showClockwiseAIMini && showClockwiseAI && (
            <Button
              sentiment={chalendarOpen ? "neutral" : "positive"}
              size="small"
              onClick={(e) => {
                setChalendarOpen((state) => !state);
                e.stopPropagation();
                void e.preventDefault;
              }}
              startIcon={chalendarOpen ? Close : AutoFixHighFilled}
            />
          )}
          {inElectronCanShowSettings && (
            <div className="cw-ml-1 cw-z-40">
              <Settings />
            </div>
          )}
        </div>
        <div
          ref={(el) => {
            setContentEl(el);
          }}
        >
          <Tabs
            tabs={tabs}
            onChange={setActiveTabFromTabsIndex}
            defaultTabIndex={orderedTabs.indexOf(clockwiseMiniTab)}
          />
        </div>
        <PlannerMiniEvent />
        {showClockwiseAIMini && showClockwiseAI && (
          <div
            className={classNames(
              "cw-z-30 cw-fixed cw-left-0 cw-right-0 cw-bg-default cw-shadow-2xl cw-rounded-t-3xl cw-transition-all cw-duration-300 cw-ease-in-out cw-transform cw-border-focusTime-emphasis-hover cw-border-2 cw-border-solid cw-overflow-auto",
              chalendarOpen ? "cw-h-[calc(100%-70px)] cw-bottom-0" : "cw-h-0 -cw-bottom-1",
              isInIframe && chalendarOpen && "cw-delay-75", // Time for popup to go max height.
              isInIframe && !chalendarOpen && "cw-transition-none", // No time to animate w/ the height receding.
            )}
          >
            <div className="cw-flex cw-items-center cw-justify-between cw-w-full cw-h-full">
              <div className="cw-flex cw-flex-col cw-items-start cw-w-full cw-h-full">
                <AIChatPlusCalendar />
              </div>
            </div>
          </div>
        )}
      </RouteRenderer>
    </PlannerProvider>
  );
};
